import * as React from 'react';
import { CheckCircle, AlertTriangle, XCircle, Flag, ChevronDown, Expand, Plus } from './Icons';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { InvoiceFields, FieldData, ItemFieldData } from '../types';
import { FieldEditDialog } from './FieldEditDialog';
import { apiService } from '../services/api';

interface WorkflowData {
  workflow_status: {
    id: string;
    workflow_type: string;
    invoice_id: string;
    invoice_number: string;
    status: string;
    last_updated: string;
    created_at: string;
    next_action_due: string;
    requester_email: string;
    action_time: string;
    notes: string;
  } | null;
  reminders: Array<{
    id: string;
    workflow_id: string;
    reminder_number: number;
    reminder_time: string;
    reminder_message: string;
  }>;
}

interface FieldsPanelProps {
  fields: InvoiceFields;
  isCollapsed: boolean;
  isProcessing: boolean;
  onFieldUpdate: (fieldKey: string, value: string | number, isItem?: boolean, itemIndex?: number) => void;
  onAddItem: () => void;
  onRemoveItem?: (itemIndex: number) => void;
  onConfirmChanges: () => void;
  onRevalidate?: () => void;
  fieldHistory: Record<string, Array<{ source: string; value: string | number }>>;
  onTriggerWorkflow?: (workflowType: string) => void;
  workflowData?: WorkflowData;
  selectedFile?: File | null;
  onEnhancedFeedback?: (feedbackData: {
    fieldName: string;
    originalValue: string | number;
    modifiedValue: string | number;
    textFeedback: string;
    screenshotUrl?: string;
    isItem?: boolean;
    itemIndex?: number;
  }) => void;
  watchTowerDataAvailable?: boolean;
}

interface FieldDisplayProps {
  field: FieldData;
  isItem?: boolean;
}

interface WatchTowerData {
  id: string;
  category: string;
  reason: string;
  filename: string;
  time_stamp: string;
  predicted_response_time: number | null;
}

const WatchTowerCard: React.FC<{ fileName?: string; fields: InvoiceFields; workflowData?: WorkflowData }> = ({ fileName, fields, workflowData }) => {
  const [watchTowerData, setWatchTowerData] = React.useState<WatchTowerData | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!fileName) {
      setWatchTowerData(null);
      return;
    }

    const fetchWatchTowerData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Generate invoice ID using the same method as in main.py
        const encoder = new TextEncoder();
        const data = encoder.encode(fileName);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        const invoiceId = hashHex.substring(0, 12);
        
        const response = await apiService.getWatchTowerData(invoiceId);
        if (response.data) {
          setWatchTowerData(response.data);
        } else {
          setError(response.error || 'Failed to fetch watch tower data');
        }
      } catch (err) {
        setError('Failed to fetch watch tower data');
        console.error('Watch tower data fetch error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWatchTowerData();
  }, [fileName]);

  if (!fileName) {
    return null;
  }

  if (isLoading) {
    return (
      <Card className="animate-fade-in-scale">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Call for Action</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-gray-500">Loading watch tower data...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !watchTowerData) {
    return (
      <Card className="animate-fade-in-scale">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Call for Action</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-gray-500">
              {error || 'No watch tower data available'}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getCriticalityColor = (category: string) => {
    switch (category) {
      case 'P1':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'P2':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCriticalityIcon = (category: string) => {
    switch (category) {
      case 'P1':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'P2':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      default:
        return <Flag className="w-4 h-4 text-gray-600" />;
    }
  };

  const formatResponseTime = (hours: number | null) => {
    if (hours === null || hours === undefined) {
      return 'N/A';
    }
    
    if (hours < 1) {
      return `${Math.round(hours * 60)} minutes`;
    } else if (hours < 24) {
      return `${Math.round(hours * 10) / 10} hours`;
    } else {
      return `${Math.round((hours / 24) * 10) / 10} days`;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const getNextBestAction = () => {
    const actions: string[] = [];

    // Check for field validation errors/anomalies
    const hasValidationErrors = fields.headers.some(field => 
      field.validationStatus === 'error' || field.validationStatus === 'warning' || field.isAnomaly
    ) || fields.items.some(item => 
      item.validationStatus === 'error' || item.validationStatus === 'warning' || item.isAnomaly
    );

    if (hasValidationErrors) {
      actions.push("Review field extraction results.");
    }

    // Check for exceptions and workflow status
    if (fields.exceptions && (fields.exceptions.exception_type || fields.exceptions.fieldValidatorExceptions?.length)) {
      const workflowInProgress = workflowData?.workflow_status?.status === 'in_progress' || 
                                workflowData?.workflow_status?.status === 'pending';
      
      if (!workflowInProgress) {
        // Determine workflow type based on exception type
        const exceptionType = fields.exceptions.exception_type;
        let workflowType = 'exception';
        
        if (exceptionType?.toLowerCase().includes('po')) {
          workflowType = 'PO mismatch';
        } else if (exceptionType?.toLowerCase().includes('gr')) {
          workflowType = 'GR';
        } else if (exceptionType?.toLowerCase().includes('non-po')) {
          workflowType = 'Non-PO';
        }
        
        actions.push(`Trigger ${workflowType} workflow`);
      } else {
        const workflowType = workflowData?.workflow_status?.workflow_type || 'current';
        actions.push(`Follow up on ${workflowType} workflow`);
      }
    }

    // Fallback action
    if (actions.length === 0) {
      actions.push("Review and post the invoice");
    }

    return actions;
  };

  return (
    <Card className="animate-fade-in-scale">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          {getCriticalityIcon(watchTowerData.category)}
          Call for Action
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          {/* Criticality Level */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-600">Criticality Level</Label>
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getCriticalityColor(watchTowerData.category)}`}>
              {watchTowerData.category === 'P1' ? 'High Priority' : 'Normal Priority'}
            </div>
          </div>

          {/* Predicted Response Time */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-600">Predicted Response Time</Label>
            <div className="text-sm font-medium">
              {formatResponseTime(watchTowerData.predicted_response_time)}
            </div>
          </div>

          {/* Assessment Time */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-600">Assessment Time</Label>
            <div className="text-sm">
              {formatTimestamp(watchTowerData.time_stamp)}
            </div>
          </div>
        </div>

        {/* Reason */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-600">Reason</Label>
          <div className={`p-3 rounded-md border text-sm ${watchTowerData.category === 'P1' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
            {watchTowerData.reason}
          </div>
        </div>

        {/* Next best action */}
        <div className="space-y-2">
          <Label className="text-sm font-bold text-gray-600">Next best action</Label>
          <div className="space-y-2">
            {getNextBestAction().map((action, index) => (
              <div 
                key={index}
                className={`p-3 rounded-md border text-sm ${watchTowerData.category === 'P1' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}
              >
                {action}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const WorkflowTab: React.FC<{
  workflowData?: WorkflowData;
  selectedFile?: File | null;
  isProcessing: boolean;
}> = ({ workflowData, selectedFile, isProcessing }) => {
  console.log('WorkflowTab render - workflowData:', workflowData);
  console.log('WorkflowTab render - selectedFile:', selectedFile?.name);
  
  if (!selectedFile) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500">
        No file selected
      </div>
    );
  }

  if (!workflowData || !workflowData.workflow_status) {
    console.log('No workflow data found. workflowData:', workflowData);
    return (
      <div className="flex items-center justify-center h-32 text-gray-500">
        <div className="text-center">
          <div>No workflow data available for this invoice</div>
          <div className="text-xs mt-2 text-gray-400">
            File: {selectedFile.name}
          </div>
          {workflowData && (
            <div className="text-xs mt-1 text-gray-400">
              Debug: {JSON.stringify(workflowData)}
            </div>
          )}
        </div>
      </div>
    );
  }

  const { workflow_status, reminders } = workflowData;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending_workflow': return 'bg-yellow-100 text-yellow-800';
      case 'complete': return 'bg-green-100 text-green-800';
      case 'error': return 'bg-red-100 text-red-800';
      case 'pending_hitl': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getWorkflowTypeLabel = (type: string) => {
    switch (type) {
      case 'PO_LINE_ITEM_MISMATCH': return 'PO Mismatch';
      case 'GR_NOT_AVAILABLE': return 'GR Not Available';
      case 'MISSING_GL_AND_CC': return 'Missing GL & Cost Center';
      case 'MISSING_GL_ACCOUNT': return 'Missing GL Account';
      case 'MISSING_COST_CENTER': return 'Missing Cost Center';
      default: return type;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Workflow Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Workflow Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-600">Workflow Type</Label>
              <div className="mt-1 text-sm">{getWorkflowTypeLabel(workflow_status.workflow_type)}</div>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Status</Label>
              <div className="mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(workflow_status.status)}`}>
                  {workflow_status.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Invoice Number</Label>
              <div className="mt-1 text-sm">{workflow_status.invoice_number || 'N/A'}</div>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Requester Email</Label>
              <div className="mt-1 text-sm">{workflow_status.requester_email || 'N/A'}</div>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Created</Label>
              <div className="mt-1 text-sm">{formatDate(workflow_status.created_at)}</div>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Last Updated</Label>
              <div className="mt-1 text-sm">{formatDate(workflow_status.last_updated)}</div>
            </div>
          </div>
          
          {workflow_status.next_action_due && (
            <div>
              <Label className="text-sm font-medium text-gray-600">Next Action Due</Label>
              <div className="mt-1 text-sm">{formatDate(workflow_status.next_action_due)}</div>
            </div>
          )}
          
          {workflow_status.notes && (
            <div>
              <Label className="text-sm font-medium text-gray-600">Notes</Label>
              <div className="mt-1 text-sm bg-gray-50 p-3 rounded-md">{workflow_status.notes}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reminders Timeline */}
      {reminders.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Reminder History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {reminders.map((reminder, index) => (
                <div key={reminder.id} className="border-l-4 border-blue-200 pl-4 py-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-600">
                      Reminder #{reminder.reminder_number}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDate(reminder.reminder_time)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700 bg-blue-50 p-3 rounded-md">
                    {reminder.reminder_message}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export const FieldsPanel: React.FC<FieldsPanelProps> = ({ 
  fields, 
  isCollapsed, 
  isProcessing,
  onFieldUpdate,
  onAddItem,
  onRemoveItem,
  onConfirmChanges,
  onRevalidate,
  fieldHistory,
  onTriggerWorkflow,
  workflowData,
  selectedFile,
  onEnhancedFeedback,
  watchTowerDataAvailable = false
}) => {
  console.log('FieldsPanel render - fields:', fields);
  console.log('FieldsPanel render - headers length:', fields.headers.length);
  console.log('FieldsPanel render - items length:', fields.items.length);
  console.log('FieldsPanel render - isProcessing:', isProcessing);
  
  const [expandedSections, setExpandedSections] = React.useState({
    headers: true,
    items: true,
    categorization: true,
    exceptions: true
  });


  const [editingField, setEditingField] = React.useState<{
    field: FieldData | null;
    isItem: boolean;
    itemIndex?: number;
  }>({ field: null, isItem: false });

  const [iconsAnimated, setIconsAnimated] = React.useState(new Set<string>());
  const [animationTrigger, setAnimationTrigger] = React.useState(0);

  // Trigger icon animations when fields change
  React.useEffect(() => {
    const fieldsWithIcons = [
      ...fields.headers.filter(f => f.hasValidation || f.isAnomaly),
      ...fields.items.filter(f => f.hasValidation || f.isAnomaly)
    ];

    if (fieldsWithIcons.length > 0) {
      // Reset animation state
      setIconsAnimated(new Set());
      setAnimationTrigger(prev => prev + 1);
      
      // Calculate grid positions and trigger animations
      fieldsWithIcons.forEach((field, index) => {
        const fieldId = 'index' in field ? `icon-${field.key}_${field.index}` : `icon-${field.key}`;
        
        // Calculate position in the grid (3 columns)
        const row = Math.floor(index / 3);
        const col = index % 3;
        
        // Calculate delay based on position (100ms per step)
        const delay = (row * 3 + col) * 100;
        
        setTimeout(() => {
          setIconsAnimated(prev => {
            const newSet = new Set(prev);
            newSet.add(fieldId);
            return newSet;
          });
        }, delay);
      });
    }
  }, [fields.headers, fields.items]);

  // Function to get animation class for icons
  const getIconAnimationClass = (fieldKey: string) => {
    const fieldId = `icon-${fieldKey}`;
    
    if (iconsAnimated.has(fieldId)) {
      return 'animate-pop-in';
    }
    
    return 'opacity-0'; // Hidden until animation triggers
  };

  const toggleSection = (section: 'headers' | 'items' | 'categorization' | 'exceptions') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Group items by index and get unique field keys for table columns
  const groupedItems = fields.items.reduce((acc, item) => {
    if (!acc[item.index]) {
      acc[item.index] = [];
    }
    acc[item.index].push(item);
    return acc;
  }, {} as Record<number, ItemFieldData[]>);

  // Get unique field keys from all items to create table columns
  const uniqueFieldKeys = Array.from(new Set(fields.items.map(item => item.key)));

  // Create table data structure
  const tableData = Object.entries(groupedItems).map(([itemIndex, itemFields]) => {
    const rowData: Record<string, ItemFieldData | undefined> = {};
    itemFields.forEach(field => {
      rowData[field.key] = field;
    });
    return {
      itemIndex: parseInt(itemIndex),
      fields: rowData
    };
  });

  if (isCollapsed) {
    return (
      <div className="h-full bg-white flex flex-col items-center py-4 gap-4">
        <div className="text-xs text-gray-600 writing-mode-vertical">Field Results</div>
      </div>
    );
  }

const toDisplayString = (value: string[] | string | null | undefined): string => {
  if (Array.isArray(value)) return value.join(', ');

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.join(', ');
      return value;
    } catch {
      return value;
    }
  }
  return '';
};


  return (
    <TooltipProvider>
      <div className="h-full bg-white dark:bg-gray-800 flex flex-col transition-colors duration-200">
        {/* Header with tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 flex-shrink-0 transition-colors duration-200">
          <Tabs defaultValue="field-results" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="field-results">Field Results</TabsTrigger>
              <TabsTrigger value="workflow">Workflow</TabsTrigger>
            </TabsList>
            
            <TabsContent value="field-results" className="h-[calc(100vh-15rem)] overflow-hidden">
              <div className="h-full flex flex-col relative">
                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6 pb-24">
                  {fields.headers.length === 0 && fields.items.length === 0 && !isProcessing && (
                    <div className="w-auto flex items-center justify-center h-32 text-gray-500 dark:text-gray-400 transition-colors duration-200">
                      <div className="text-center">
                        <div className="text-2xl mb-2">📋</div>
                        <div className="text-gray-700 dark:text-gray-300">No fields extracted yet</div>
                        <div className="text-xs mt-1 text-gray-500 dark:text-gray-400">Upload an invoice to begin</div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-6">
                    {/* Watch Tower Card - Only show after Watch Tower Agent runs */}
                    {watchTowerDataAvailable && <WatchTowerCard fileName={selectedFile?.name} fields={fields} workflowData={workflowData} />}

                    {/* Header Information Card */}
                    {fields.headers.length > 0 && (
                      <Card className="animate-fade-in-scale">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center justify-between">
                            <div className="flex items-center gap-1">
                              Header Information
                              <span className="text-sm text-gray-500">({fields.headers.length})</span>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => toggleSection('headers')}
                            >
                              {expandedSections.headers ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <Expand className="w-4 h-4" />
                              )}
                            </Button>
                          </CardTitle>
                        </CardHeader>
                        
                        {expandedSections.headers && (
                          <CardContent className="space-y-4">
                            <div className="grid grid-cols-3 gap-4">
                              {fields.headers.map((field, index) => (
                                <div key={`header-${index}`} className={`space-y-2 ${field.isAnimating ? 'animate-pulse' : ''}`}>
                                  <Label className="text-sm font-medium flex items-center gap-1">
                                    {field.hasValidation && field.validationStatus === 'success' && (
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <div className={`w-2 h-2 rounded-full bg-green-500 ${getIconAnimationClass(field.key)}`}></div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>{field.validationReason || 'Field validation passed'}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                    {field.hasValidation && field.validationStatus === 'warning' && (
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <AlertTriangle className={`w-3 h-3 text-yellow-500 ${getIconAnimationClass(field.key)}`} />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p className="max-w-xs">
                                            <strong>Warning:</strong><br />
                                            {field.validationReason || 'Field validation warning'}
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                    {field.hasValidation && field.validationStatus === 'error' && (
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <XCircle className={`w-3 h-3 text-red-500 ${getIconAnimationClass(field.key)}`} />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p className="max-w-xs">
                                            <strong>Validation Failed:</strong><br />
                                            {field.validationReason || 'Field validation failed'}
                                            {field.expectedValue && field.expectedValue !== 'None' && (
                                              <>
                                                <br /><br />
                                                <strong>Expected:</strong> {field.expectedValue}
                                              </>
                                            )}
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                    {field.isAnomaly && (
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <Flag className={`w-3 h-3 text-red-500 ${getIconAnimationClass(field.key)}`} />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p className="max-w-xs">
                                            <strong>Anomaly Detected:</strong><br />
                                            {field.anomalyReason || 'This field was flagged as anomalous'}
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                    {field.key}
                                  </Label>
                                  <Input
                                    value={field.value || ''}
                                    readOnly
                                    placeholder="N/A"
                                    className={`bg-gray-50 cursor-pointer hover:bg-gray-100 ${field.isAnimating ? 'bg-blue-50 border-blue-200' : ''}`}
                                    onDoubleClick={() => setEditingField({ field, isItem: false })}
                                  />
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    )}

                    {/* Line Information Card - Now with Tabular View */}
                    {Object.keys(groupedItems).length > 0 && (
                      <Card className="animate-fade-in-scale">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center justify-between">
                            <div className="flex items-center gap-1">
                              Line Information
                              <span className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-200">({Object.keys(groupedItems).length} items)</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-6 px-2 text-xs"
                                onClick={onAddItem}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add Item
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6"
                                onClick={() => toggleSection('items')}
                              >
                                {expandedSections.items ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <Expand className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </CardTitle>
                        </CardHeader>
                        
                        {expandedSections.items && (
                          <CardContent className="p-0">
                            <div className="overflow-hidden">
                              <table className="w-full dark:border-gray-600">
                                <thead>
                                  <tr className="border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 transition-colors duration-200">
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[60px] transition-colors duration-200">
                                      Action
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[80px] transition-colors duration-200">
                                      Item #
                                    </th>
                                    {uniqueFieldKeys.map((fieldKey) => (
                                      <th key={fieldKey} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[120px] transition-colors duration-200">
                                        {fieldKey}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600 transition-colors duration-200">
                                  {tableData.map((row) => (
                                    <tr key={row.itemIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                                      <td className="px-4 py-3 border-gray-200 dark:border-gray-600">
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              variant="destructive"
                                              size="sm"
                                              className="h-8 w-8 p-0"
                                              onClick={() => onRemoveItem?.(row.itemIndex)}
                                              disabled={isProcessing || !onRemoveItem}
                                            >
                                              <XCircle className="h-5 w-5" />
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>Remove line item</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </td>
                                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 transition-colors duration-200">
                                        {row.itemIndex + 1}
                                      </td>
                                      {uniqueFieldKeys.map((fieldKey) => {
                                        const field = row.fields[fieldKey];
                                        return (
                                          <td key={`${row.itemIndex}-${fieldKey}`} className="px-4 py-3 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 transition-colors duration-200">
                                            {field ? (
                                              <div className="flex items-center gap-2">
                                                {/* Validation/Anomaly Icons */}
                                                {field.isAnomaly && (
                                                  <Tooltip>
                                                    <TooltipTrigger>
                                                      <Flag className={`w-3 h-3 text-red-500 flex-shrink-0 ${getIconAnimationClass(`${field.key}_${field.index}`)}`} />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                      <p className="max-w-xs">
                                                        <strong>Anomaly Detected:</strong><br />
                                                        {field.anomalyReason || 'This field was flagged as anomalous'}
                                                      </p>
                                                    </TooltipContent>
                                                  </Tooltip>
                                                )}
                                                {field.hasValidation && field.validationStatus === 'success' && (
                                                  <Tooltip>
                                                    <TooltipTrigger>
                                                      <div className={`w-2 h-2 rounded-full bg-green-500 flex-shrink-0 ${getIconAnimationClass(`${field.key}_${field.index}`)}`}></div>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                      <p>{field.validationReason || 'Field validation passed'}</p>
                                                    </TooltipContent>
                                                  </Tooltip>
                                                )}
                                                {field.hasValidation && field.validationStatus === 'warning' && (
                                                  <Tooltip>
                                                    <TooltipTrigger>
                                                      <AlertTriangle className={`w-3 h-3 text-yellow-500 flex-shrink-0 ${getIconAnimationClass(`${field.key}_${field.index}`)}`} />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                      <p className="max-w-xs">
                                                        <strong>Warning:</strong><br />
                                                        {field.validationReason || 'Field validation warning'}
                                                      </p>
                                                    </TooltipContent>
                                                  </Tooltip>
                                                )}
                                                {field.hasValidation && field.validationStatus === 'error' && (
                                                  <Tooltip>
                                                    <TooltipTrigger>
                                                      <XCircle className={`w-3 h-3 text-red-500 flex-shrink-0 ${getIconAnimationClass(`${field.key}_${field.index}`)}`} />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                      <p className="max-w-xs">
                                                        <strong>Validation Failed:</strong><br />
                                                        {field.validationReason || 'Field validation failed'}
                                                        {field.expectedValue && field.expectedValue !== 'None' && (
                                                          <>
                                                            <br /><br />
                                                            <strong>Expected:</strong> {field.expectedValue}
                                                          </>
                                                        )}
                                                      </p>
                                                    </TooltipContent>
                                                  </Tooltip>
                                                )}
                                                
                                                {/* Editable Field Value */}
                                                <Input
                                                  value={field.value || ''}
                                                  readOnly
                                                  placeholder="N/A"
                                                  className={`bg-gray-50 cursor-pointer hover:bg-gray-100 border-none shadow-none text-sm h-8 min-w-0 ${field.isAnimating ? 'bg-blue-50 border-blue-200' : ''}`}
                                                  onDoubleClick={() => setEditingField({ field, isItem: true, itemIndex: field.index })}
                                                />
                                              </div>
                                            ) : (
                                              <span className="text-gray-400 text-sm">N/A</span>
                                            )}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    )}

                    {/* Invoice Categorization Card */}
                    {fields.categorization && (
                      <Card className="animate-fade-in-scale">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center justify-between">
                            <div className="flex items-center gap-1">
                              Invoice Categorization
                            </div>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => toggleSection('categorization')}
                            >
                              {expandedSections.categorization ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <Expand className="w-4 h-4" />
                              )}
                            </Button>
                          </CardTitle>
                        </CardHeader>
                        
                        {expandedSections.categorization && (
                          <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                            {fields.categorization.category && (
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Category</Label>
                                <Input
                                  value={fields.categorization.category}
                                  readOnly
                                  className="bg-gray-50"
                                />
                              </div>
                            )}
                            {fields.categorization.region && (
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Region</Label>
                                <Input
                                  value={fields.categorization.region}
                                  readOnly
                                  className="bg-gray-50"
                                />
                              </div>
                            )}
                            {fields.categorization.country && (
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Country</Label>
                                <Input
                                  value={fields.categorization.country}
                                  readOnly
                                  className="bg-gray-50"
                                />
                              </div>
                            )}
                            {fields.categorization.invoice_type && fields.categorization.invoice_type.length > 0 && (
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Invoice Type</Label>
                                <Input
                                  value={toDisplayString(fields.categorization.invoice_type)}
                                  readOnly
                                  className="bg-gray-50"
                                />
                              </div>
                            )}
                            {fields.categorization.invoice_category && fields.categorization.invoice_category.length > 0 && (
                              <div className="space-y-2 col-span-2">
                                <Label className="text-sm font-medium">Invoice Category</Label>
                                <Input
                                  value={toDisplayString(fields.categorization.invoice_category)}
                                  readOnly
                                  className="bg-gray-50"
                                />
                              </div>
                            )}
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    )}

                    {/* Exception Handling Card */}
                    {fields.exceptions && (
                      <Card className="animate-fade-in-scale">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center justify-between">
                            <div className="flex items-center gap-1">
                              Exception Handling
                            </div>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => toggleSection('exceptions')}
                            >
                              {expandedSections.exceptions ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <Expand className="w-4 h-4" />
                              )}
                            </Button>
                          </CardTitle>
                        </CardHeader>
                        
                        {expandedSections.exceptions && (
                          <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 gap-4">
                              {/* Exception Handler Agent Exceptions */}
                              <div>
                                <Label className="text-sm font-medium mb-2 block">Exception Handler Agent found Exceptions:</Label>
                                <div className={`mb-3 p-3 rounded border ${
                                  fields.exceptions.exception_type === 'NO_EXCEPTIONS' 
                                    ? 'bg-green-50 border-green-200' 
                                    : 'bg-orange-50 border-orange-200'
                                }`}>
                                  <div className={`font-medium mb-1 ${
                                    fields.exceptions.exception_type === 'NO_EXCEPTIONS' 
                                      ? 'text-green-700' 
                                      : 'text-orange-700'
                                  }`}>
                                    {fields.exceptions.exception_type || 'NO_EXCEPTIONS'}
                                  </div>
                                  {fields.exceptions.message && (
                                    <div className="text-sm text-gray-600">
                                      {fields.exceptions.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Field Validator Agent Exceptions */}
                              {fields.exceptions.fieldValidatorExceptions && fields.exceptions.fieldValidatorExceptions.length > 0 && (
                                <div className="border-t pt-4 mt-4">
                                  <Label className="text-sm font-medium mb-2 block">Field Validator Agent found Exceptions:</Label>
                                  {fields.exceptions.fieldValidatorExceptions.map((exc, index) => (
                                    <div key={index} className="mb-3 p-3 bg-orange-50 rounded border border-orange-200">
                                      <div className="font-medium text-orange-700 mb-1">{exc.type}</div>
                                      {exc.type === 'PO_LINE_ITEM_MISMATCH' && (
                                        <div className="text-sm text-gray-600 mb-2">
                                          Line items do not match PO database
                                        </div>
                                      )}
                                      {exc.type === 'GR_NOT_AVAILABLE' && (
                                        <div className="text-sm text-gray-600 mb-2">
                                          Goods receipt not available for these items
                                        </div>
                                      )}
                                      {exc.type === 'TAX_CODE_MISMATCH' && (
                                        <div className="text-sm text-gray-600 mb-2">
                                          Tax code does not match expected values
                                        </div>
                                      )}
                                      
                                      {/* Workflow Trigger Button */}
                                      {onTriggerWorkflow && (
                                        <div className="mt-2">
                                          <Button 
                                            onClick={() => onTriggerWorkflow(exc.type)}
                                            size="sm"
                                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1"
                                            disabled={isProcessing}
                                          >
                                            Trigger {exc.type === 'PO_LINE_ITEM_MISMATCH' ? 'PO Mismatch' : 
                                                     exc.type === 'GR_NOT_AVAILABLE' ? 'GR' : 
                                                     'Non-PO'} Workflow
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    )}

                    {/* Action Buttons */}
                    {!isProcessing && (fields.headers.length > 0 || fields.items.length > 0) && (
                      <div className="flex justify-end gap-3">
                        {onRevalidate && (
                          <Button 
                            onClick={onRevalidate}
                            size="lg"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            Revalidate
                          </Button>
                        )}
                        <Button 
                          onClick={onConfirmChanges}
                          size="lg"
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          Confirm Changes and Post
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="workflow" className="mt-4 h-[calc(100vh-12rem)] overflow-y-auto">
              <WorkflowTab 
                workflowData={workflowData}
                selectedFile={selectedFile}
                isProcessing={isProcessing}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    
    {/* Field Edit Dialog */}
    <FieldEditDialog
      isOpen={editingField.field !== null}
      onClose={() => setEditingField({ field: null, isItem: false })}
      field={editingField.field}
      fieldHistory={editingField.field ? (
        editingField.isItem && editingField.itemIndex !== undefined
          ? (fieldHistory[`${editingField.field.key}_${editingField.itemIndex}`] || [])
          : (fieldHistory[editingField.field.key] || [])
      ) : []}
      onSave={async (value, feedback) => {
        if (editingField.field) {
          onFieldUpdate(
            editingField.field.key, 
            value, 
            editingField.isItem, 
            editingField.itemIndex
          );
          
          // Handle enhanced feedback if provided
          if (feedback && (feedback.textFeedback || feedback.screenshot) && onEnhancedFeedback) {
            let screenshotUrl = undefined;
            
            // Upload screenshot if provided
            if (feedback.screenshot) {
              const uploadResponse = await apiService.uploadScreenshot(feedback.screenshot);
              if (uploadResponse.data) {
                screenshotUrl = uploadResponse.data.url;
              } else {
                console.error('Failed to upload screenshot:', uploadResponse.error);
              }
            }
            
            // Call the enhanced feedback handler
            onEnhancedFeedback({
              fieldName: editingField.field.key,
              originalValue: editingField.field.value,
              modifiedValue: value,
              textFeedback: feedback.textFeedback,
              screenshotUrl,
              isItem: editingField.isItem,
              itemIndex: editingField.itemIndex
            });
          }
          
          setEditingField({ field: null, isItem: false });
        }
      }}
    />
    </TooltipProvider>
  );
};